import { supabase } from "@/integrations/supabase/client";
import { resizeAndCompressImage, resizeToSquare, validateImageFile, isHEICFile } from "@/utils/imageUtils";

export const photoUploadService = {
  async uploadPhoto(file: File, userId: string, isMainPhoto: boolean = false): Promise<string> {
    // Validate file
    const validationError = validateImageFile(file);
    if (validationError) {
      throw new Error(validationError);
    }

    // Additional server-side validation
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      throw new Error("File size must be less than 10MB");
    }

    // Enhanced file type validation with HEIC/HEIF support
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'];
    
    // Check MIME type first, then fallback to extension for HEIC files
    const isValidType = allowedTypes.includes(file.type);
    const fileName = file.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
    
    if (!isValidType && !hasValidExtension) {
      throw new Error("Invalid file type. Only JPEG, PNG, WebP, and HEIC files are allowed.");
    }

    try {
      // Process image based on whether it's the main photo or not
      const processedFile = isMainPhoto 
        ? await resizeToSquare(file, 512) // Square format for main profile photo
        : await resizeAndCompressImage(file, 800, true); // Maintain aspect ratio for additional photos
      
      // Generate secure filename with user ID validation
      if (!userId || typeof userId !== 'string') {
        throw new Error("Invalid user ID");
      }
      
      const fileExt = 'jpg'; // Always save as JPEG
      const timestamp = Date.now();
      const randomSuffix = Math.random().toString(36).substring(2);
      const fileName = `${userId}/${timestamp}-${randomSuffix}.${fileExt}`;
      
      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('alpha')
        .upload(fileName, processedFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Upload error:', error);
        throw new Error(`Upload failed: ${error.message}`);
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('alpha')
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (processingError: any) {
      console.error('Image processing error:', processingError);
      
      // Provide specific error message for HEIC files
      if (isHEICFile(file) && processingError.message.includes('browser')) {
        throw new Error('HEIC files from iPhone may not be supported in all browsers. Please try converting to JPEG first or use a different browser.');
      }
      
      throw new Error(processingError.message || 'Failed to process image. Please try a different image or convert to JPEG format.');
    }
  },

  async deletePhoto(photoUrl: string, userId: string): Promise<void> {
    if (!userId || typeof userId !== 'string') {
      throw new Error("Invalid user ID");
    }

    if (!photoUrl || typeof photoUrl !== 'string') {
      throw new Error("Invalid photo URL");
    }

    // Extract filename from URL and validate it belongs to the user
    const urlParts = photoUrl.split('/');
    const fileName = urlParts[urlParts.length - 1];
    const pathUserId = urlParts[urlParts.length - 2];
    
    // Security check: ensure user can only delete their own photos
    if (pathUserId !== userId) {
      throw new Error("Unauthorized: You can only delete your own photos");
    }
    
    const fullPath = `${userId}/${fileName}`;
    
    // Validate file path to prevent directory traversal
    if (fullPath.includes('..') || fullPath.includes('./')) {
      throw new Error("Invalid file path");
    }
    
    // Delete from storage
    const { error } = await supabase.storage
      .from('alpha')
      .remove([fullPath]);

    if (error) {
      console.error('Delete error:', error);
      throw new Error(`Delete failed: ${error.message}`);
    }
  }
};
